/**
 * Copyright 2022 Phenix Real Time Solutions, Inc. All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {h, Component, cloneElement} from 'preact';
import {Children} from 'preact-compat';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import VideoControls from '../components/controls/index.jsx';
import {isVolumeSliderShown, isQualityListShown, areControlsShown, getTimeoutId, getVideoQualitySetting} from '../reducers/controls';
import {
  changeMuteState,
  changeVideoEnabledStateAction,
  updateMuteStateAction,
  changeFullscreen,
  changeVolume,
  rewindByAction
} from '../actions/video';
import {
  showVolumeSliderAction,
  hideVolumeSliderAction,
  showControlsAndThenHide,
  toggleVideoQualitySettingsAction,
  setVideoQualitySettingAction
} from '../actions/controls';
import {getAudioMuted, getVideoEnabled, getVolume, isFullscreen} from '../reducers/video';
import {getLag} from '../reducers/statistics';

import SeekbarContainer from './SeekbarContainer.jsx';
import PlayButton from '../components/controls/PlayButton.jsx';
import RewindButton from '../components/controls/RewindButton.jsx';
import MuteVideoButton from '../components/controls/MuteVideoButton.jsx';
import VolumeButtonAndSlider from '../components/controls/VolumeButtonAndSlider.jsx';
import LatencyText from '../components/controls/LatencyText.jsx';
import FullScreenButton from '../components/controls/FullScreenButton.jsx';
import VideoQualityButton from '../components/controls/VideoQualityButton.jsx';

const defaultShowControlsWhenPlayingInterval = 5000;
const defaultShowControlsWhenPausedInterval = 120000;

class VideoControlsContainer extends Component {
  constructor(props) {
    super(props);

    this.renderer = null;
    this.lastTimeCalled = 0;
    this.listener = null;
  }

  debouncedShowControls() {
    this.debounce(() => this.props.showControls(this.props.timeoutId), 500);
  }

  debounce(callback, timeout) {
    if (this.lastTimeCalled + timeout > Date.now()) {
      return;
    }

    this.lastTimeCalled = Date.now();

    return callback();
  }

  getLeftColumnChildren() {
    return Children.toArray(this.props.children)
      .filter((child) => {
        return (
          child.type === MuteVideoButton ||
          child.key === 'MuteVideoButton' ||
          child.type === VolumeButtonAndSlider ||
          child.key === 'VolumeButtonAndSlider' ||
          child.type === RewindButton ||
          child.key === 'RewindButton' ||
          child.type === LatencyText ||
          child.key === 'LatencyText'
        );
      })
      .map((child) => cloneElement(child, this.props, child.props.children));
  }

  getRightColumnChildren() {
    return Children.toArray(this.props.children)
      .filter((child) => {
        return (
          child.type === FullScreenButton ||
          child.key === 'FullScreenButton' ||
          child.type === VideoQualityButton ||
          child.key === 'VideoQualityButton'
        );
      })
      .map((child) => cloneElement(child, this.props, child.props.children));
  }

  getFloatingChildren() {
    return Children.toArray(this.props.children)
      .filter((child) => {
        return child.type === PlayButton || child.key === 'PlayButton';
      })
      .map((child) => cloneElement(child, this.props, child.props.children));
  }

  getSeekbar() {
    const seekbar = Children.toArray(this.props.children).find((child) => {
      return child.type === SeekbarContainer || child.key === 'SeekbarContainer';
    });

    return seekbar ? cloneElement(seekbar, this.props, seekbar.props.children) : null;
  }

  componentDidMount() {
    this.debouncedShowControls();
  }

  componentDidUpdate() {
    if (this.props.video && !this.listener && this.props.video.getContainer()) {
      this.listener = () => this.debouncedShowControls();
      this.props.video.getContainer().addEventListener('mousemove', this.listener);
      this.props.video.getContainer().addEventListener('touchstart', this.listener);
    }
  }

  componentWillUnmount() {
    if (this.props.video && this.props.video.getContainer()) {
      this.props.video.getContainer().removeEventListener('mousemove', this.listener);
      this.props.video.getContainer().removeEventListener('touchstart', this.listener);
    }

    this.listener = null;
  }

  render() {
    const {showNativeControls, areControlsShown} = this.props;

    return (
      <VideoControls
        areControlsShown={areControlsShown}
        showNativeControls={showNativeControls}
        showControls={() => this.debouncedShowControls()}
        leftColumnControls={this.getLeftColumnChildren()}
        rightColumnControls={this.getRightColumnChildren()}
        floatingControls={this.getFloatingChildren()}
        seekbarControl={this.getSeekbar()}
        selectedVideoQuality={this.props.selectedVideoQuality}
      />
    );
  }
}

VideoControlsContainer.propTypes = {
  amountToRewindStream: PropTypes.number.isRequired,
  areControlsShown: PropTypes.bool.isRequired,
  canAudioStateBeChanged: PropTypes.bool.isRequired,
  canVideoStateBeChanged: PropTypes.bool.isRequired,
  children: PropTypes.arrayOf(PropTypes.object),
  isLiveOrRtmpStream: PropTypes.bool.isRequired,
  isQualityListShown: PropTypes.bool.isRequired,
  isVideoEnabled: PropTypes.bool.isRequired,
  isVolumeSliderShown: PropTypes.bool.isRequired,
  lag: PropTypes.number.isRequired,
  mustStartMuted: PropTypes.bool.isRequired,
  muted: PropTypes.bool.isRequired,
  showNativeControls: PropTypes.bool.isRequired,
  timeoutId: PropTypes.number,
  video: PropTypes.object,
  volume: PropTypes.number.isRequired,

  changeFullscreenState: PropTypes.func.isRequired,
  changeMuteState: PropTypes.func.isRequired,
  updateMuteState: PropTypes.func.isRequired,
  changeVideoEnabledState: PropTypes.func.isRequired,
  changeVolume: PropTypes.func.isRequired,
  hideVolumeSlider: PropTypes.func.isRequired,
  rewindStream: PropTypes.func.isRequired,
  showControls: PropTypes.func.isRequired,
  showVolumeSlider: PropTypes.func.isRequired,
  toggleVideoSettings: PropTypes.func.isRequired,
  selectedVideoQuality: PropTypes.string.isRequired
};

const mapStateToProps = (state, {video}) => {
  return {
    amountToRewindStream: 10,
    areControlsShown: areControlsShown(state),
    canAudioStateBeChanged: video ? video.canAudioStateBeChanged() : true,
    canVideoStateBeChanged: video ? video.canVideoStateBeChanged() : true,
    isFullscreen: isFullscreen(state),
    isLiveOrRtmpStream: video ? video.isLiveOrRtmpStream() : false,
    isQualityListShown: isQualityListShown(state),
    isVideoEnabled: getVideoEnabled(state),
    isVolumeSliderShown: isVolumeSliderShown(state),
    lag: getLag(state),
    mustStartMuted: video ? video.mustStartMuted() : false,
    muted: getAudioMuted(state),
    showNativeControls: video ? video.shouldShowNativeControls() : false,
    timeoutId: getTimeoutId(state),
    video,
    volume: getVolume(state),
    selectedVideoQuality: getVideoQualitySetting(state)
  };
};

const changeFullscreenState = (dispatch, video, isFullscreen) => {
  if (video) {
    if (isFullscreen) {
      video.requestFullscreen();
      video.addListenerForExitFullscreen(() => dispatch(changeFullscreen(false)));
    } else {
      video.exitFullscreen();
    }
  }

  dispatch(changeFullscreen(isFullscreen));
};

const mapDispatchToProps = (dispatch, {video}) => {
  return {
    changeFullscreenState: (isFullscreen) => changeFullscreenState(dispatch, video, isFullscreen),
    changeMuteState: (muted) => dispatch(changeMuteState(video, muted)),
    updateMuteState: () => dispatch(updateMuteStateAction(video)),
    changeVideoEnabledState: (videoState) => dispatch(changeVideoEnabledStateAction(video, videoState)),
    changeVolume: (volume) => dispatch(changeVolume(video, volume)),
    hideVolumeSlider: () => dispatch(hideVolumeSliderAction()),
    rewindStream: (amountToRewind) => dispatch(rewindByAction(video, amountToRewind)),
    showControls: (timeoutId) =>
      dispatch(
        showControlsAndThenHide(
          timeoutId,
          video && (video.isPaused() || video.getAudioMuted())
            ? defaultShowControlsWhenPausedInterval
            : defaultShowControlsWhenPlayingInterval
        )
      ),
    showVolumeSlider: () => dispatch(showVolumeSliderAction()),
    toggleVideoSettings: () => dispatch(toggleVideoQualitySettingsAction()),
    setVideoQuality: (selectedQuality) => dispatch(setVideoQualitySettingAction(selectedQuality))
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(VideoControlsContainer);